import FeedbackLoader from '@mail/feedback-loader';
import EventsLogger from '@mail/feedback-loader/EventsLogger';
import { createAction } from '@reduxjs/toolkit';
import config from 'Cloud/config';
import { xray } from 'lib/xray';
import { IS_AUTO_TEST_HIDE, IS_PHONE_BROWSER, IS_TABLET_BROWSER } from 'reactApp/appHelpers/configHelpers';
import { isDarkTheme } from 'reactApp/appHelpers/featuresHelpers';
import {
    type CsatRawDetails,
    type CsatSurvey,
    CSAT_DOMAIN,
    CSAT_FORCE_SHOW_SURVEY_ID,
    CSAT_LOGGER_OPTIONS,
    CSAT_SURVEYS_RAW,
    DEFAULT_PLATFORMS,
} from 'reactApp/appHelpers/featuresHelpers/features/csat';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { store } from 'reactApp/store';
import { sendGa } from 'reactApp/utils/ga';
import { call, put } from 'redux-saga/effects';
import { getStorageFromUri } from 'server/helpers/getRequestParams';
import { select } from 'typed-redux-saga';

import { addPromoToShowQueue, promoShown, removePromo } from '../promo.module';
import { PromoSelectors } from '../promo.selectors';
import { EPromoType } from '../promo.types';

const PROMO_ID = 'csat-survey';

export const enum CSAT_TRIGGER_EVENTS {
    editingDocument = 'editingDocument',
    editingPresentation = 'editingPresentation',
    editingSpreadsheet = 'editingSpreadsheet',
    openItem = 'openItem',
    uploadItem = 'uploadItem',
    searchItem = 'searchItem',
    createFile = 'createFile',
    createFolder = 'createFolder',
    addToFavorites = 'addToFavorites',
    downloadItem = 'downloadItem',
    copyWeblink = 'copyWeblink',
    closeWeblink = 'closeWeblink',
}

export const pushCSATTriggerEvent = createAction<{ type: CSAT_TRIGGER_EVENTS }>('promo/pushCSATTriggerEvent');

const sendCSATGa = (action: string): void => sendGa('csat-survey', action);

let loader: FeedbackLoader | null = null;
let eventsLogger: EventsLogger<CSAT_TRIGGER_EVENTS> | null = null;

const radar = (name, data) => {
    const params: any = { p: 'cloud-web-analytics' };

    if (data) {
        params.dwh = data;
    }

    xray.send(name, params);
};

const CORSAPI_HOST = config.get('CORS_API_HOST');

const generateProject = (storage: EStorageType) => {
    return `cloud_web_${storage}`;
};

const checkSurveysPlatform = (item: CsatRawDetails) => {
    const allowed = item.platforms || DEFAULT_PLATFORMS;
    if (IS_PHONE_BROWSER) {
        return allowed.includes('phone');
    }

    if (IS_TABLET_BROWSER) {
        return allowed.includes('tablet');
    }

    return allowed.includes('desktop');
};

const mapDetails = (raw: CsatRawDetails[], storage: EStorageType): { feedbacks: CsatSurvey[] } => {
    if (Array.isArray(raw)) {
        return raw.reduce(
            (acc, item) => {
                const allowedByStorage = item.points.includes(storage);
                const allowedByPlatform = checkSurveysPlatform(item);
                if (allowedByStorage && allowedByPlatform) {
                    acc.feedbacks.push(...(item.config.feedbacks || []));
                }

                return acc;
            },
            { feedbacks: [] } as { feedbacks: CsatSurvey[] }
        );
    }

    return { feedbacks: [] };
};

/**
 * Конфиг в облаке
 * https://omicron.mail.ru/projects/41/applications/83/parameters/9560/edit
 */
export function* initCSATSurvey() {
    const currentStorage = yield* select(getCurrentStorage);
    const storageFromUrl = getStorageFromUri(window.location.pathname) as EStorageType | undefined;
    const storage = currentStorage === EStorageType.editor && storageFromUrl ? storageFromUrl : currentStorage;
    const isAnonymous = yield* select(UserSelectors.isAnonymous);

    if (IS_AUTO_TEST_HIDE || !CSAT_SURVEYS_RAW || isAnonymous) {
        return;
    }

    const { feedbacks } = mapDetails(CSAT_SURVEYS_RAW, storage);

    if (!feedbacks.length) {
        return;
    }

    const email = yield* select(UserSelectors.getEmail);

    eventsLogger = new EventsLogger();

    loader = new FeedbackLoader(feedbacks || [], {
        forceShowSurveyId: CSAT_FORCE_SHOW_SURVEY_ID,
        apiBaseUrl: `https://${CORSAPI_HOST}/api/v1/`,
        loggerOptions: CSAT_LOGGER_OPTIONS,
        surveyLoaderOptions: {
            eventsLogger,
            email: email ?? undefined,
            radar,
            /* Отключить опрос можно из омикрона, просто убрав его из списка или permille: 0 */
            checkFeature: () => true,
            frameLoaderOptions: {
                domain: CSAT_DOMAIN,
                isMobile: IS_PHONE_BROWSER,
                styles: IS_PHONE_BROWSER
                    ? {
                          zIndex: '1206',
                          width: 'calc(100% - 16px)',
                          right: '8px',
                          left: '8px',
                          bottom: '8px',
                      }
                    : {
                          /* Приподнять над футером */
                          bottom: '37px',
                          zIndex: '1206',
                      },
                /* Для разделения аналитики */
                project: generateProject(storage),
            },
        },
    });

    yield put(
        addPromoToShowQueue({
            type: EPromoType.csatSurvey,
            promoId: PROMO_ID,
            onShow: () => {
                sendCSATGa('show');
                store.dispatch(promoShown(EPromoType.csatSurvey));
            },
            onClose: () => {},
        })
    );
}

export function* showCSATSurvey() {
    const csatSurvey = yield* select(PromoSelectors.getPromo(EPromoType.csatSurvey));

    const onMessage = (e: MessageEvent<any>) => {
        const { action, type } = e?.data || {};

        if (type === 'feedback-evt' && action === 'close') {
            store.dispatch(removePromo(EPromoType.csatSurvey));
            sendCSATGa('close');
            window.removeEventListener('message', onMessage);
        }
    };

    loader
        ?.update()
        .then(() => {
            csatSurvey?.onShow();
            window.addEventListener('message', onMessage);
        })
        .catch((error) => {
            // eslint-disable-next-line no-console
            console.debug('[csat-feedback]', error);

            store.dispatch(removePromo(EPromoType.csatSurvey));
            sendCSATGa('skip');
        });

    loader?.setDarkTheme(isDarkTheme);
}

export function* handlePushCSATTriggerEvent({ payload }: ReturnType<typeof pushCSATTriggerEvent>) {
    if (eventsLogger) {
        yield call(eventsLogger.push.bind(eventsLogger), payload.type);
    }
}

export function closeCSATSurvey() {
    loader?.close();
}
